/*
| Attendance matrix detail-day modal. Clicking a worked cell fetches that
| employee's shift chronology for the day (PRÁCE rows + check-in/out + totals).
| The mobile person picker is upgraded to a searchable combobox.
*/
import { attachTip } from './tooltip';
import { enhanceSelect, ComboItem } from './combobox';
import { avatarEl } from './avatar';
import { skeletonLines } from './skeleton';
import { screenSignal } from './screenScope';
import { toast } from './toast';

const axios = (): any => (window as any).axios;
const esc = (v: unknown): string => String(v ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
));
const hours = (min: number): string => (min / 60).toLocaleString('cs-CZ', { maximumFractionDigits: 2 });

const DOW = ['PO', 'ÚT', 'ST', 'ČT', 'PÁ', 'SO', 'NE'];
const fmtDay = (iso: string): string => {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return `${DOW[(date.getDay() + 6) % 7]} ${d}.${m}. ${y}`;
};

const ttRow = (label: string, val: string): string => `<div class="tt-row"><span class="tt-muted">${esc(label)}</span><span>${val}</span></div>`;
const statTip = (rows: [string, string][]): string =>
    `<div class="tt-stat">${rows.map(([l, v]) => `<div class="tt-srow"><span class="tt-muted">${esc(l)}</span><span>${esc(v)}</span></div>`).join('')}</div>`;
const buildCellTip = (cell: HTMLElement): string => {
    const d = cell.dataset;
    const rows = [ttRow('Kdo', esc(d.name ?? ''))];
    if (d.ttType) rows.push(ttRow('Typ', esc(d.ttType)));
    if (d.ttWp) rows.push(ttRow('Pracoviště', esc(d.ttWp)));
    if (d.ttNet) rows.push(ttRow('Čistě', esc(d.ttNet)));
    return `<div class="tt-emp"><span class="tt-title">${esc(d.ttDate ?? '')} · ${esc(d.ttTime ?? '')}</span><div class="tt-rows">${rows.join('')}</div></div>`;
};

export function initAttendance(): void {
    const root = document.querySelector<HTMLElement>('[data-attendance]');
    if (!root) return;
    initPrefill();
    const dayUrl = root.dataset.attendance!;
    const calendarBase = root.dataset.calendarBase ?? '';
    const punchBase = root.dataset.punchBase ?? '';
    const modal = document.querySelector<HTMLElement>('[data-att-modal]')!;
    const body = document.querySelector<HTMLElement>('[data-att-body]')!;
    const subtitle = document.querySelector<HTMLElement>('[data-att-subtitle]')!;

    const close = (): void => { modal.hidden = true; };
    document.querySelector('[data-att-close]')?.addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) close(); }, { signal: screenSignal() });

    root.querySelectorAll<HTMLElement>('[data-att-cell]').forEach((cell) => {
        cell.addEventListener('click', () => openDay(cell.dataset.user!, cell.dataset.date!, cell.dataset.name!, cell.dataset.color ?? 'gray', cell.dataset.initials ?? ''));
        attachTip(cell, () => buildCellTip(cell));
    });

    // Rich tooltip on the worked/fund/diff summary column.
    const sumLabels = (window as any).planningAttendanceI18n?.summary ?? {};
    root.querySelectorAll<HTMLElement>('.dz-sum[data-tt-worked]').forEach((sum) => {
        attachTip(sum, () => statTip([
            [sumLabels.worked ?? 'Odpracováno', `${sum.dataset.ttWorked} h`],
            [sumLabels.fund ?? 'Fond', `${sum.dataset.ttFond} h`],
            [sumLabels.diff ?? 'Rozdíl', `${sum.dataset.ttDiff} h`],
        ]));
    });

    // Mobile person picker → searchable combobox; the existing change listener
    // (which toggles the day lists) keeps working via the dispatched change event.
    const dzm = document.querySelector<HTMLSelectElement>('[data-dzm-select]');
    if (dzm) {
        const items: ComboItem[] = [...dzm.options].map((o) => ({
            value: o.value, label: o.textContent ?? '', color: o.dataset.color, initials: o.dataset.initials, sublabel: o.dataset.sub,
        }));
        enhanceSelect(dzm, { items });
    }

    const labels = (window as any).planningAttendanceI18n ?? {};

    async function openDay(userId: string, date: string, name: string, color: string, initials: string): Promise<void> {
        subtitle.hidden = true; // name + date now live in dd-head inside the body
        body.innerHTML = skeletonLines(4);
        modal.hidden = false;
        try {
            const { data } = await axios().get(dayUrl, { params: { user_id: userId, date } });
            body.innerHTML = renderDay(data, name, color, initials);
            body.querySelector('[data-dd-close]')?.addEventListener('click', close);
            body.querySelector('[data-dd-plan]')?.addEventListener('click', () => { window.location.href = `${calendarBase}/${data.date}`; });
            bindPunches(body);
        } catch {
            body.innerHTML = `<div class="dd-empty">${esc(labels.error ?? 'Error')}</div>`;
        }
    }

    function renderDay(data: any, name: string, color: string, initials: string): string {
        const head = `<div class="dd-head">${avatarEl(initials, color, 'md')}<h2 class="text-base font-semibold">${esc(name)}</h2><span class="dd-date">${esc(fmtDay(data.date))}</span></div>`;
        if (!data.shifts.length) return `${head}<div class="dd-empty">${esc(labels.no_shifts ?? '—')}</div>`;

        // Each shift carries editable check-in / check-out punches (disabled when the
        // viewer may not edit that slot). Changes save immediately via bindPunches().
        const punchField = (type: 'in' | 'out', val: string | null, can: boolean, by: string | null): string => {
            const label = type === 'in' ? (labels.check_in ?? 'Příchod') : (labels.check_out ?? 'Odchod');
            const byLine = by ? `<span class="dd-by">${esc(labels.filled_by ?? '')}: ${esc(by)}</span>` : '';
            return `<label class="dd-punch-f"><span class="c-label">${esc(label)}</span>`
                + `<input type="time" data-punch="${type}" value="${esc(val ?? '')}" ${can ? '' : 'disabled'}>${byLine}</label>`;
        };
        const rows = data.shifts.map((s: any) => `
            <li class="dd-item" data-shift="${esc(s.id)}">
                <div class="dd-item-head">
                    <span class="dd-type">${esc(s.type ?? labels.work ?? '')}</span>
                    <span>${esc(s.from)}–${esc(s.to)}${s.workplace ? ' · ' + esc(s.workplace) : ''}</span>
                    <span class="dd-hours">${hours(s.net)} h</span>
                </div>
                <div class="dd-punch">
                    ${punchField('in', s.checked_in_at, !!s.can_in, s.checked_in_by)}
                    ${punchField('out', s.checked_out_at, !!s.can_out, s.checked_out_by)}
                    <span class="dd-punch-status" data-punch-status hidden></span>
                </div>
            </li>`).join('');

        const totals = `
            <div class="dd-totals">
                <span>${esc(labels.gross ?? 'Hrubě')} <strong>${hours(data.gross)} h</strong></span>
                <span>${esc(labels.breaks ?? 'Pauzy')} <strong>${hours(data.break)} h</strong></span>
                <span>${esc(labels.net ?? 'Čistě')} <strong>${hours(data.net)} h</strong></span>
            </div>`;

        const actions = `
            <div class="dd-actions">
                <button type="button" class="btn-cta" data-dd-plan>${esc(labels.edit_in_plan ?? 'Upravit v plánu')}</button>
                <button type="button" class="f-cancel" data-dd-close>${esc(labels.close ?? 'Zavřít')}</button>
            </div>`;

        return `${head}<ul class="dd-list">${rows}</ul>${totals}${actions}`;
    }

    // Save a punch immediately on change: a value sets the time, an empty value clears.
    function bindPunches(scope: HTMLElement): void {
        scope.querySelectorAll<HTMLElement>('[data-shift]').forEach((li) => {
            const id = li.getAttribute('data-shift');
            const status = li.querySelector<HTMLElement>('[data-punch-status]');
            li.querySelectorAll<HTMLInputElement>('[data-punch]').forEach((input) => {
                input.addEventListener('change', async () => {
                    const type = input.dataset.punch; // 'in' | 'out'
                    const body = input.value ? { type, at: input.value } : { type, clear: 1 };
                    try {
                        await axios().post(`${punchBase}/${id}/punch`, body);
                        if (status) { status.hidden = false; status.textContent = labels.saved ?? 'OK'; status.className = 'dd-punch-status dd-punch-status--ok'; }
                    } catch (e: any) {
                        if (status) { status.hidden = false; status.textContent = e?.response?.data?.message ?? labels.error ?? 'Error'; status.className = 'dd-punch-status dd-punch-status--err'; }
                    }
                });
            });
        });
    }
}

/*
| "Předvyplnit z plánu" — copy planned times into empty punches for the shown
| month. Confirm first; the result toast reports filled/skipped counts.
*/
function initPrefill(): void {
    const btn = document.querySelector<HTMLElement>('[data-att-prefill]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
        if (!window.confirm(btn.dataset.prefillConfirm ?? '')) return;
        btn.setAttribute('disabled', 'true');
        try {
            const { data } = await axios().post(btn.dataset.prefillUrl, { month: btn.dataset.prefillMonth });
            toast(data.message);
            window.setTimeout(() => window.location.reload(), 1200);
        } catch (e: any) {
            btn.removeAttribute('disabled');
            toast(e?.response?.data?.message ?? 'Error');
        }
    });
}
